import { Callout, H5, Icon, Intent } from '@blueprintjs/core';
import * as React from 'react';
import { Text } from 'vet-bones/components';

import {
  DataSourceType,
  UNKNOWN_DATA_SOURCE_TYPE,
} from 'src/constants/dataSourceTypeDescription';
import { TEST_IDS } from 'src/constants/testIds';
import { connectionSelector } from 'src/selectors/connection';
import { currentStardogVersionSelector } from 'src/selectors/currentStardogVersion';
import { dataSourceLabelsByTypeSelector } from 'src/selectors/dataSourceLabelsByType';
import { DataSource } from 'src/store/state/dataSources';
import { components } from 'src/templates/copy';
import { getDataSourceIcon, icons } from 'src/templates/icons';
import { colors } from 'src/templates/styles/colors';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export const DataSourcePanelContainer: React.VFC<{
  children?: React.ReactNode;
  dataSource?: DataSource;
  type?: DataSourceType;
}> = ({ children, dataSource, type = null }) => {
  const connection = useReduxSelector(connectionSelector);

  let dataSourceInfo = null;
  if (dataSource) {
    // TODO: Need a more general approach to showing this
    // information when non-JDBC data sources are supported
    dataSourceInfo = (
      <>
        <b
          data-testid={
            TEST_IDS.projectResourceDialog.dataSourcePanel.dataSourceInfo
          }
        >
          <Text ellipsize>{dataSource.id}</Text>
        </b>
        <Text ellipsize>
          {components.projectResourceDialog.projectDataSourcePanel.endpoint}:{' '}
          {dataSource.options?.['jdbc.url'] || ''}
        </Text>
        <Text ellipsize>
          {components.projectResourceDialog.projectDataSourcePanel.username}:{' '}
          {dataSource.options?.['jdbc.username'] || ''}
        </Text>
      </>
    );
  }

  const dataSourceLabelsByType = useReduxSelector(
    dataSourceLabelsByTypeSelector(connection)
  );
  const stardogVersion = useReduxSelector(currentStardogVersionSelector);
  let typeInfo = null;
  if (type) {
    const { isBlueprintIcon, icon } = getDataSourceIcon({
      dataSourceType: type,
      stardogVersion,
    });
    const label =
      dataSourceLabelsByType[type] ||
      dataSourceLabelsByType[UNKNOWN_DATA_SOURCE_TYPE];
    typeInfo = (
      <div className="sd-data-source-panel__type-info">
        {isBlueprintIcon ? (
          <Icon icon={icon} iconSize={24} />
        ) : (
          <img alt={`${label} icon`} height="24px" src={icon} />
        )}
        <div
          data-testid={TEST_IDS.projectResourceDialog.dataSourcePanel.typeInfo}
        >
          {label}
        </div>
      </div>
    );
  }

  return (
    <Callout className="sd-data-source-panel" intent={Intent.NONE}>
      <div className="sd-data-source-panel__connection-info">
        <div className="sd-data-source-panel__connection-info__column">
          <div className="sd-data-source-panel__connection-name">
            <Icon
              color={colors.darkIcon}
              icon={icons.connection}
              iconSize={24}
            />
            <H5
              data-testid={
                TEST_IDS.projectResourceDialog.dataSourcePanel.connectionInfo
              }
            >
              {connection?.name}
            </H5>
          </div>
          {typeInfo}
        </div>
        <div className="sd-data-source-panel__connection-info__column">
          {dataSourceInfo}
        </div>
      </div>
      {children ? (
        <div className="sd-data-source-panel__body">{children}</div>
      ) : null}
    </Callout>
  );
};
